import {
  ActionBarPrimitive,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
  useThread,
} from "@assistant-ui/react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUp,
  CopyIcon,
  Mic,
  Square,
  Volume2Icon,
  VolumeOffIcon,
} from "lucide-react";
import { domAnimation, LazyMotion, MotionConfig } from "motion/react";
import * as m from "motion/react-m";
import type { FC, PropsWithChildren } from "react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ComposerAddAttachment,
  ComposerAttachments,
  UserMessageAttachments,
} from "@/components/assistant-ui/attachment";
import { ConfirmationCard } from "@/components/assistant-ui/confirmation-card";
import { IntentRequest } from "@/components/assistant-ui/intent-request";
import { MessageSuggestions } from "@/components/assistant-ui/message-suggestions";
import { MultiAgentText } from "@/components/assistant-ui/multi-agent-text";
import { SystemInteractionToolFallback } from "@/components/assistant-ui/system-interaction-group";
import { ThinkingIndicator } from "@/components/assistant-ui/thinking-indicator";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { VoiceComposer } from "@/components/assistant-ui/voice-composer";
import { Button } from "@/components/ui/button";

import { cn } from "@/lib/utils";

const INTENT_TOOL_NAMES = new Set([
  "manage_service",
  "manage_category",
  "sort_items",
  "batch_update_services",
  "bulk_update_services",
  "copy_services",
  "update_worker_services",
  "batch_create_services",
  "batch_create_categories",
  "batch_update_categories",
  "commit_config_change",
  "update_operating_hours",
  "update_salon_profile",
  "update_salon_contact",
  "create_holiday",
  "update_holiday",
  "create_team_member",
  "update_team_member",
  "update_worker_service_restrictions",
  "create_appointment",
  "update_appointment",
  "batch_update_appointments",
  "update_appointment_status",
  "assign_worker",
  "create_customer",
  "create_discount",
  "update_discount",
  "delete_discount",
]);

type IntentToolPart = {
  type: "tool-call";
  toolCallId: string;
  toolName: string;
  args: unknown;
  result?: unknown;
};

const isIntentToolPart = (part: unknown): part is IntentToolPart => {
  if (!part || typeof part !== "object") {
    return false;
  }

  const toolPart = part as Record<string, unknown>;
  return (
    toolPart.type === "tool-call" &&
    typeof toolPart.toolCallId === "string" &&
    typeof toolPart.toolName === "string" &&
    INTENT_TOOL_NAMES.has(toolPart.toolName)
  );
};

export const Thread: FC = () => {
  return (
    <LazyMotion features={domAnimation}>
      <MotionConfig reducedMotion="user">
        <ThreadPrimitive.Root
          className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
          style={{
            ["--thread-max-width" as string]: "56rem",
          }}
        >
          <ThreadPrimitive.Viewport className="aui-thread-viewport relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll px-2">
            <ThreadPrimitive.If empty>
              <ThreadWelcome />
            </ThreadPrimitive.If>

            <ThreadPrimitive.Messages
              components={{
                UserMessage,
                EditComposer,
                AssistantMessage,
              }}
            />

            <ThreadPrimitive.If running>
              <ThinkingIndicator />
            </ThreadPrimitive.If>

            <ThreadPrimitive.If empty={false}>
              <div className="aui-thread-viewport-spacer min-h-8 grow" />
            </ThreadPrimitive.If>

            <Composer />
          </ThreadPrimitive.Viewport>
        </ThreadPrimitive.Root>
      </MotionConfig>
    </LazyMotion>
  );
};

const ThreadScrollToBottom: FC = () => {
  const { t } = useTranslation("ai-chat");

  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip={t("thread.actions.scrollToBottom")}
        variant="outline"
        className="aui-thread-scroll-to-bottom absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible dark:bg-background dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  const { t } = useTranslation("ai-chat");

  return (
    <div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-[var(--thread-max-width)] flex-grow flex-col">
      <div className="aui-thread-welcome-center flex w-full flex-grow flex-col items-center justify-center">
        <div className="aui-thread-welcome-message flex size-full flex-col justify-center px-8">
          <m.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="aui-thread-welcome-message-motion-1 text-2xl font-semibold"
          >
            {t("thread.welcome.greeting")}
          </m.div>
          <m.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.1 }}
            className="aui-thread-welcome-message-motion-2 text-2xl text-muted-foreground/65"
          >
            {t("thread.welcome.subtitle")}
          </m.div>
        </div>
      </div>
      <ThreadSuggestions />
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  const { t } = useTranslation("ai-chat");

  const suggestions = [
    {
      key: "createAppointment",
      title: t("starterPrompts.createAppointment.title"),
      label: t("starterPrompts.createAppointment.label"),
      prompt: t("starterPrompts.createAppointment.prompt"),
    },
    {
      key: "todayAppointments",
      title: t("starterPrompts.todayAppointments.title"),
      label: t("starterPrompts.todayAppointments.label"),
      prompt: t("starterPrompts.todayAppointments.prompt"),
    },
    {
      key: "operatingHours",
      title: t("starterPrompts.operatingHours.title"),
      label: t("starterPrompts.operatingHours.label"),
      prompt: t("starterPrompts.operatingHours.prompt"),
    },
    {
      key: "onlineBooking",
      title: t("starterPrompts.onlineBooking.title"),
      label: t("starterPrompts.onlineBooking.label"),
      prompt: t("starterPrompts.onlineBooking.prompt"),
    },
  ];

  return (
    <div className="aui-thread-welcome-suggestions grid w-full gap-2 pb-4 @md:grid-cols-2">
      {suggestions.map((suggestion, index) => (
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.05 * index }}
          key={`suggested-action-${suggestion.key}`}
          className="aui-thread-welcome-suggestion-display"
        >
          <ThreadPrimitive.Suggestion prompt={suggestion.prompt} send asChild>
            <Button
              variant="ghost"
              className="aui-thread-welcome-suggestion h-auto w-full flex-1 flex-wrap items-start justify-start gap-1 rounded-3xl border px-5 py-4 text-left text-sm @md:flex-col dark:hover:bg-accent/60"
              aria-label={suggestion.prompt}
            >
              <span className="aui-thread-welcome-suggestion-text-1 font-medium">
                {suggestion.title}
              </span>
              <span className="aui-thread-welcome-suggestion-text-2 text-muted-foreground">
                {suggestion.label}
              </span>
            </Button>
          </ThreadPrimitive.Suggestion>
        </m.div>
      ))}
    </div>
  );
};

const Composer: FC = () => {
  const [isVoiceMode, setIsVoiceMode] = useState(false);

  const handleVoiceTranscript = useCallback((transcript: string) => {
    // Exit voice mode first to show the text input
    setIsVoiceMode(false);

    // Use setTimeout to ensure the DOM is updated before we try to access elements
    setTimeout(() => {
      // Get the composer input element and set its value
      const input = document.querySelector<HTMLTextAreaElement>(".aui-composer-input");
      if (!input) {
        console.error("[Thread] Could not find composer input element");
        return;
      }

      // Set the value using the native setter to bypass React's controlled input
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value"
      )?.set;

      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, transcript);
      } else {
        input.value = transcript;
      }

      // Trigger both input and change events to ensure React detects the change
      const inputEvent = new Event("input", { bubbles: true });
      const changeEvent = new Event("change", { bubbles: true });
      input.dispatchEvent(inputEvent);
      input.dispatchEvent(changeEvent);

      // Focus the input so user can review and send
      input.focus();
    }, 50);
  }, []);

  const handleVoiceCancel = useCallback(() => {
    setIsVoiceMode(false);
  }, []);

  return (
    <div className="aui-composer-wrapper sticky bottom-0 mx-auto flex w-full max-w-[var(--thread-max-width)] flex-col gap-4 overflow-visible rounded-t-3xl bg-background pb-4 md:pb-6">
      <ThreadScrollToBottom />

      {/* Message suggestions - shown above composer */}
      <MessageSuggestions />

      {isVoiceMode ? (
        // Voice recording UI
        <VoiceComposer onTranscript={handleVoiceTranscript} onCancel={handleVoiceCancel} />
      ) : (
        // Text input UI
        <ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col rounded-3xl border border-border bg-muted px-1 pt-2 shadow-[0_9px_9px_0px_rgba(0,0,0,0.01),0_2px_5px_0px_rgba(0,0,0,0.06)] dark:border-muted-foreground/15">
          <ComposerAttachments />
          <ComposerInput onVoiceClick={() => setIsVoiceMode(true)} />
        </ComposerPrimitive.Root>
      )}
    </div>
  );
};

interface ComposerInputProps {
  onVoiceClick?: () => void;
}

const ComposerInput: FC<ComposerInputProps> = ({ onVoiceClick }) => {
  const { t } = useTranslation("ai-chat");

  return (
    <>
      <ComposerPrimitive.Input
        placeholder={t("thread.placeholder")}
        className="aui-composer-input mb-1 max-h-32 min-h-16 w-full resize-none bg-transparent px-3.5 pt-1.5 pb-3 text-base outline-none placeholder:text-muted-foreground focus:outline-primary"
        rows={1}
        autoFocus
        aria-label={t("thread.aria.messageInput")}
      />
      <ComposerAction onVoiceClick={onVoiceClick} />
    </>
  );
};

interface ComposerActionProps {
  onVoiceClick?: () => void;
}

const ComposerAction: FC<ComposerActionProps> = ({ onVoiceClick }) => {
  const { t } = useTranslation("ai-chat");
  return (
    <div className="aui-composer-action-wrapper relative mx-1 mt-2 mb-2 flex items-center justify-between">
      <div className="flex items-center gap-1">
        <ComposerAddAttachment />

        {/* Microphone button */}
        {onVoiceClick && (
          <TooltipIconButton
            tooltip={t("thread.actions.voiceInput")}
            side="top"
            variant="ghost"
            size="icon"
            className="size-[34px]"
            onClick={onVoiceClick}
            aria-label={t("thread.aria.voiceInput")}
          >
            <Mic className="size-5" />
          </TooltipIconButton>
        )}
      </div>

      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip={t("thread.actions.sendMessage")}
            side="bottom"
            type="submit"
            variant="default"
            size="icon"
            className="aui-composer-send size-[34px] rounded-full p-1"
            aria-label={t("thread.aria.sendMessage")}
          >
            <ArrowUpIcon className="aui-composer-send-icon size-5" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>

      <ThreadPrimitive.If running>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel size-[34px] rounded-full border border-muted-foreground/60 hover:bg-primary/75 dark:border-muted-foreground/90"
            aria-label={t("thread.aria.stopGenerating")}
          >
            <Square className="aui-composer-cancel-icon size-3.5 fill-white dark:fill-black" />
          </Button>
        </ComposerPrimitive.Cancel>
      </ThreadPrimitive.If>
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const AssistantIntentCards: FC = () => {
  const message = useMessage();
  const messages = useThread((state) => state.messages);

  const intentParts = useMemo(() => {
    if (message.role !== "assistant") {
      return [];
    }

    const intentsByMessageId = new Map<string, IntentToolPart[]>();
    const seenToolCallIds = new Set<string>();

    for (let i = 0; i < messages.length; i++) {
      const currentMessage = messages[i];
      if (currentMessage.role !== "assistant") {
        continue;
      }

      const currentIntentParts = currentMessage.content.reduce<IntentToolPart[]>((acc, part) => {
        if (isIntentToolPart(part) && !seenToolCallIds.has(part.toolCallId)) {
          seenToolCallIds.add(part.toolCallId);
          acc.push(part);
        }
        return acc;
      }, []);

      if (currentIntentParts.length === 0) {
        continue;
      }

      const hasTextContent = currentMessage.content.some(
        (part) => part.type === "text" && part.text.trim().length > 0
      );

      let targetMessageId = currentMessage.id;

      // Some streams emit tool calls in a separate assistant message before text.
      // Move those cards to the next assistant message so actions stay at the visible response footer.
      if (!hasTextContent) {
        for (let j = i + 1; j < messages.length; j++) {
          if (messages[j]?.role === "assistant") {
            targetMessageId = messages[j].id;
            break;
          }
        }
      }

      const existing = intentsByMessageId.get(targetMessageId) ?? [];
      intentsByMessageId.set(targetMessageId, [...existing, ...currentIntentParts]);
    }

    return intentsByMessageId.get(message.id) ?? [];
  }, [messages, message.id, message.role]);

  if (message.role !== "assistant") {
    return null;
  }

  if (intentParts.length === 0) {
    return null;
  }

  const IntentRequestCard = IntentRequest as unknown as FC<{
    toolName: string;
    args: unknown;
    result?: unknown;
  }>;

  return (
    <div className="mt-3">
      {intentParts.map((part) => (
        <IntentRequestCard
          key={part.toolCallId}
          toolName={part.toolName}
          args={part.args}
          result={part.result}
        />
      ))}
    </div>
  );
};

/**
 * ToolGroup component wraps consecutive tool calls in an expandable card
 * Shows user-friendly summary with collapsible technical details
 */
const ToolGroup: FC<PropsWithChildren<{ startIndex: number; endIndex: number }>> = ({
  children,
}) => {
  const { t } = useTranslation("ai-chat");
  const [isExpanded, setIsExpanded] = useState(false);

  // Count the number of tool calls (children)
  const childArray = Array.isArray(children) ? children : [children];
  const toolCount = childArray.filter(Boolean).length;

  if (toolCount === 0) return null;

  const _operationsLabel =
    toolCount === 1 ? t("systemInteraction.operations") : t("systemInteraction.operations_plural");

  return (
    <div className="aui-tool-group my-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>
          {t("systemInteraction.complete")} {toolCount}
        </span>
        {isExpanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
      </button>

      {isExpanded && <div className="mt-2 flex flex-wrap gap-1.5">{children}</div>}
    </div>
  );
};

const AssistantMessage: FC = () => {
  return (
    <MessagePrimitive.Root asChild>
      <div
        className="aui-assistant-message-root relative mx-auto w-full max-w-[var(--thread-max-width)] animate-in py-4 duration-150 ease-out fade-in slide-in-from-bottom-1 last:mb-24"
        data-role="assistant"
      >
        <div className="aui-assistant-message-content mx-2 leading-7 break-words text-foreground">
          <MultiAgentText />

          {/* Render all non-intent tools in the system interaction group */}
          <MessagePrimitive.Parts
            components={{
              Text: () => null, // Skip default text rendering (handled by MultiAgentText)
              ToolGroup, // Wrap tool calls in expandable card
              tools: {
                by_name: {
                  confirmationCard: ConfirmationCard,
                  manage_service: SystemInteractionToolFallback,
                  manage_category: SystemInteractionToolFallback,
                  sort_items: SystemInteractionToolFallback,
                  batch_update_services: SystemInteractionToolFallback,
                  bulk_update_services: SystemInteractionToolFallback,
                  copy_services: SystemInteractionToolFallback,
                  update_worker_services: SystemInteractionToolFallback,
                  batch_create_services: SystemInteractionToolFallback,
                  batch_create_categories: SystemInteractionToolFallback,
                  commit_config_change: SystemInteractionToolFallback,
                  update_operating_hours: SystemInteractionToolFallback,
                  update_salon_profile: SystemInteractionToolFallback,
                  update_salon_contact: SystemInteractionToolFallback,
                  create_holiday: SystemInteractionToolFallback,
                  update_holiday: SystemInteractionToolFallback,
                  create_team_member: SystemInteractionToolFallback,
                  update_team_member: SystemInteractionToolFallback,
                  update_worker_service_restrictions: SystemInteractionToolFallback,
                },
                Fallback: SystemInteractionToolFallback, // Show tools with compact badges
              },
            }}
          />
          <MessageError />
          <AssistantIntentCards />
        </div>

        <div className="aui-assistant-message-footer mt-2 ml-2 flex">
          {/* <BranchPicker /> */}
          <AssistantActionBar />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  const { t } = useTranslation("ai-chat");

  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      autohideFloat="single-branch"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground data-floating:absolute data-floating:rounded-md data-floating:border data-floating:bg-background data-floating:p-1 data-floating:shadow-sm"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip={t("thread.actions.copy")}>
          <MessagePrimitive.If copied>
            <CheckIcon />
          </MessagePrimitive.If>
          <MessagePrimitive.If copied={false}>
            <CopyIcon />
          </MessagePrimitive.If>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Speak asChild>
        <TooltipIconButton tooltip={t("thread.actions.readAloud")}>
          <Volume2Icon />
        </TooltipIconButton>
      </ActionBarPrimitive.Speak>
      <ActionBarPrimitive.StopSpeaking asChild>
        <TooltipIconButton tooltip={t("thread.actions.stopReading")}>
          <VolumeOffIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.StopSpeaking>
    </ActionBarPrimitive.Root>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root asChild>
      <div
        className="aui-user-message-root mx-auto grid w-full max-w-[var(--thread-max-width)] animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] gap-y-2 px-2 py-4 duration-150 ease-out fade-in slide-in-from-bottom-1 first:mt-3 last:mb-5 [&:where(>*)]:col-start-2"
        data-role="user"
      >
        <UserMessageAttachments />

        <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
          <div className="aui-user-message-content rounded-3xl bg-muted px-5 py-2.5 break-words text-foreground">
            <MessagePrimitive.Parts />
          </div>
          <div className="aui-user-action-bar-wrapper absolute top-1/2 left-0 -translate-x-full -translate-y-1/2 pr-2">
            <UserActionBar />
          </div>
        </div>

        {/* <BranchPicker className="aui-user-branch-picker col-span-full col-start-1 row-start-3 -mr-1 justify-end" /> */}
      </div>
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return null; // Edit disabled - users cannot re-edit message history
  // return (
  //   <ActionBarPrimitive.Root
  //     hideWhenRunning
  //     autohide="not-last"
  //     className="aui-user-action-bar-root flex flex-col items-end"
  //   >
  //     <ActionBarPrimitive.Edit asChild>
  //       <TooltipIconButton tooltip="Edit" className="aui-user-action-edit p-4">
  //         <PencilIcon />
  //       </TooltipIconButton>
  //     </ActionBarPrimitive.Edit>
  //   </ActionBarPrimitive.Root>
  // );
};

const EditComposer: FC = () => {
  const { t } = useTranslation("ai-chat");

  return (
    <div className="aui-edit-composer-wrapper mx-auto flex w-full max-w-[var(--thread-max-width)] flex-col gap-4 px-2 first:mt-4">
      <ComposerPrimitive.Root className="aui-edit-composer-root ml-auto flex w-full max-w-7/8 flex-col rounded-xl bg-muted">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input flex min-h-[60px] w-full resize-none bg-transparent p-4 text-foreground outline-none"
          autoFocus
        />

        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center justify-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm" aria-label={t("thread.aria.cancelEdit")}>
              {t("thread.actions.cancel")}
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm" aria-label={t("thread.aria.updateMessage")}>
              {t("thread.actions.update")}
            </Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </div>
  );
};

const _BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({ className, ...rest }) => {
  const { t } = useTranslation("ai-chat");

  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root mr-2 -ml-2 inline-flex items-center text-xs text-muted-foreground",
        className
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={t("thread.actions.previous")}>
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={t("thread.actions.next")}>
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
